// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { STORE_USERNAME_KEY } from "@/app.constant";
import {
  Button,
  type canvasRefProps,
  Captcha,
  Footer,
  PabbitLogo,
} from "@/components";
import { Input, InputWithClear } from "@/components/form";
import { useFormErrorsToast } from "@/hooks";
import { usePreSignIn } from "@/queries";
import { errorToast } from "@/shared";
import { checkTokenExpiration } from "@/shared/requests/auth";
import { setStorage } from "@/shared/store";
import Consent from "./components/Consent";

interface LoginForm {
  phone: string;
  captcha: string;
}

const Login = () => {
  const { watch, register, setValue, getValues, handleSubmit } =
    useForm<LoginForm>();
  const navigate = useNavigate();

  useEffect(() => {
    checkTokenExpiration().then(() => {
      navigate("/");
    });

  }, [navigate]);

  const { mutateAsync: preSignIn, isLoading: isPreSignInLoading } =
    usePreSignIn();
  const captchaRef = useRef<canvasRefProps>(null);
  const [code, setCode] = useState("");

  const { toastFormValueError } = useFormErrorsToast();
  const phoneValue = watch("phone") ?? "";
  const captchaValue = watch("captcha") ?? "";
  const canProceed = Boolean(phoneValue) && captchaValue.length === 4;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, "").substring(0, 11);
    setValue("phone", cleaned);
  };

  const handleVarifyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\W/g, "").substring(0, 4);
    setValue("captcha", cleaned);
  };

  const handleLogin = async (data: LoginForm) => {
    const { phone } = data;
    window.localStorage.setItem(STORE_USERNAME_KEY, phone);
    preSignIn({ phone })
      .then(() => {
        setStorage("isAutoSendedVerifyCode", false);
        navigate("/login-confirm");})
      .catch((error: Error) => {
        errorToast(error.message ?? error.toString());
      });
  };

  return (
    <div className="contents">
      <div className="flex flex-1 flex-col px-8 py-12">
        <PabbitLogo />
        <form className="relative mt-10 flex flex-col gap-5">
          <InputWithClear
            styleType="shadow"
            register={register("phone", {
              onChange: handlePhoneChange,
              required: "電話番号の入力が必須です",
              pattern: {
                value: /^\d{11}$/gm,
                message: "11桁の番号を入力してください",
              },
            })}
            setValue={setValue}
            getValues={getValues}
            placeholder="電話番号を入力"
          />
          <div>
            <div className="relative">
              <Input
                styleType="shadow"
                register={register("captcha", {
                  onChange: handleVarifyCodeChange,
                  required: "認証文字の入力が必須です",
                  validate(value) {
                    if (value.toLowerCase() !== code.toLowerCase()) {
                      return "認証文字のエラー";
                    }
                  },
                })}
                placeholder="右側の4桁の数字を入力"
                inputMode="numeric"
                type="text"
                pattern="[0-9]*"
                className="pr-[7.5rem]"
              />
              <Captcha
                className="absolute right-[8px] top-1/2 -translate-y-1/2"
                ref={captchaRef}
                charNum={4}
                onChange={setCode}
                onClick={captchaRef.current?.refresh}
              />
            </div>
            <p className="mt-2.5 text-[14px] font-normal leading-[100%] tracking-normal text-[#6D6D78]">
              画像の数字がはっきり見えない時は、画像をタップして表示を更新します。
            </p>
          </div>
        </form>
        <div className="mt-10">
          <Button
            className="w-full"
            size="large"
            loading={isPreSignInLoading}
            onClick={handleSubmit(handleLogin, toastFormValueError)}
            disabled={!canProceed}
          >
            次へ
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default function LoginWrapper() {
  const consentFromStorage =
    window.localStorage.getItem("pabbit_configurator_user_consent_confirm") ||
    "false";
  const isConsent = consentFromStorage?.toLowerCase() !== "false";
  const [isUserConsent, setIsUserConsent] = useState(isConsent);
  if (!isUserConsent) {
    return <Consent setIsUserConsent={setIsUserConsent} />;
  } else return <Login />;
}
